import AsyncControl from '@/Controls/AsyncControl';
import ComponentManager from '@/Management/ComponentManager';
import PropertyGroupBoxViewModelType from '@/ViewModels/Controls/PropertyGroupBoxViewModel';
import LayoutRenderer from '@/Utility/LayoutRenderer';
import ControlFactory from '@/Factories/ControlFactory';
import GroupFactory from '@/Factories/GroupFactory';
import ControlFactoryTuple from '@/Factories/ControlFactoryTuple';
import GroupFactoryTuple from '@/Factories/GroupFactoryTuple';
import { IDetailViewGroupDTO, IDVGItemDTO } from "coreframetypings/dto";
import {DisposeBehavior} from '#/DefaultViewExtensionInterfaces';

export default class PropertyGroupBox extends AsyncControl {

    private jQueryControl: JQuery;
    private content: JQuery;

    public constructor(private viewModel: PropertyGroupBoxViewModelType, private element?: JQuery) {
        super(viewModel, element);
    }

    public async getElementAsync(): Promise<JQuery> {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element || $('<div>');
        this.content = $('<div class="c-content">').appendTo(this.jQueryControl);

        const tuples: Array<ControlFactoryTuple | GroupFactoryTuple> = [];

        const createdItems = await this.createItemsAsync(this.viewModel.detailViewItems, this.createDVItemAsync);
        if (this.isDisposed || this.viewModel.isDisposed)
            return null;

        for (const { controlFactory, tuple } of createdItems) {
            tuples.push(tuple);
            this.viewModel.createdFactories.push(controlFactory);
        }

        const createdGroups = await this.createItemsAsync(this.viewModel.detailViewGroups, this.createGroupAsync);
        if (this.isDisposed || this.viewModel.isDisposed)
            return null;

        for (const { groupFactory, tuple } of createdGroups) {
            tuples.push(tuple);
            this.viewModel.createdFactories.push(groupFactory);
        }

        const $layout = await this.createItemAsync(null, () => this.createLayout(tuples.filter(t => t != null)));
        if (this.isDisposed || this.viewModel.isDisposed)
            return null;

        this.content.append($layout);

        this.applyBindings(this.jQueryControl, {
            controlName: 'propertygroupbox',

            loader: this.viewModel.isBusy,

            controlIdent: this.identifier,

            visible: this.definePureComputed({
                read: () => {
                    const vmVisible = this.viewModel.isVisible();
                    const anyFactoryVisible = tuples.filter(t => t?.factory?.isVisible()).length > 0;
                    return vmVisible || anyFactoryVisible;
                }
            })
        });

        this.widget(null);

        return this.jQueryControl;
    }

    private async createDVItemAsync(detailViewItem: IDVGItemDTO) {
        return ControlFactory.createInstanceAsync(detailViewItem, this.viewModel.context, {
            detailViewItem: detailViewItem,
            isEnabled: this.viewModel.isEnabled,
            isSummaryViewMode: this.viewModel.isSummaryViewMode,
            locator: this.viewModel.locator
        });
    }


    private async createGroupAsync(detailViewGroup: IDetailViewGroupDTO) {
        return GroupFactory.createInstanceAsync(detailViewGroup, this.viewModel.context, {
            isSummaryViewMode: this.viewModel.isSummaryViewMode,
            locator: this.viewModel.locator
        });
    }

    private createLayout(tuples: (ControlFactoryTuple | GroupFactoryTuple)[]) {
        return LayoutRenderer.renderLayout(this.viewModel.layout, tuples, this.viewModel.context.labelColumnWidth);
    }

    protected getDisposeBehavior(): DisposeBehavior {
        return "Asynchronous";
    }
}

ComponentManager.setControl('PropertyGroupBox', PropertyGroupBox, 'RADPresentation.HTMLClient.DefaultViewExtensions');